import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, Quote } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-slate-300"
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <section className="py-16 lg:py-24 bg-[#0F172A]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#F59E0B] font-medium text-sm uppercase tracking-wider mb-4">
              Testimonials
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-64 rounded-lg bg-slate-700"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-[#0F172A]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#F59E0B] font-medium text-sm uppercase tracking-wider mb-4">
            Testimonials
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
            What Our Clients Say
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base lg:text-lg">
            Don't just take our word for it. Here's what our commercial partners 
            and clients have to say about working with Gap Pro.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card
              key={testimonial.id}
              className="bg-slate-800/50 border-slate-700 testimonial-card relative overflow-hidden"
              data-testid={`testimonial-card-${testimonial.id}`}
            >
              <CardContent className="p-8">
                {/* Quote Icon */}
                <Quote className="w-10 h-10 text-[#F59E0B]/30 absolute top-6 right-6" />
                
                {/* Rating */}
                <div className="flex gap-1 mb-6">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Content */}
                <p className="text-slate-300 leading-relaxed mb-8 relative z-10">
                  "{testimonial.content}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <Avatar className="w-12 h-12 border-2 border-[#F59E0B]">
                    <AvatarImage src={testimonial.image_url} alt={testimonial.client_name} />
                    <AvatarFallback className="bg-[#F59E0B] text-[#0F172A] font-bold">
                      {testimonial.client_name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-white font-medium">{testimonial.client_name}</p>
                    <p className="text-slate-400 text-sm">{testimonial.role}</p>
                    <p className="text-[#F59E0B] text-sm font-medium">{testimonial.company}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <p className="font-heading text-4xl font-bold text-[#F59E0B] stat-number">500+</p>
            <p className="text-slate-400 mt-2">Projects Completed</p>
          </div>
          <div>
            <p className="font-heading text-4xl font-bold text-[#F59E0B] stat-number">15+</p>
            <p className="text-slate-400 mt-2">Years Experience</p>
          </div>
          <div>
            <p className="font-heading text-4xl font-bold text-[#F59E0B] stat-number">100%</p>
            <p className="text-slate-400 mt-2">Client Satisfaction</p>
          </div>
          <div>
            <p className="font-heading text-4xl font-bold text-[#F59E0B] stat-number">50+</p>
            <p className="text-slate-400 mt-2">Commercial Partners</p>
          </div>
        </div>
      </div>
    </section>
  );
};
